import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/addons/loaders/DRACOLoader.js';
import { clone as cloneSkinned } from 'three/addons/utils/SkeletonUtils.js';
import { gsap } from 'gsap';
import { vertex, fragment } from './shaders.js';

// Flip to true to bring up the live-tuning GUI during development.
const SHOW_DEBUG_GUI = false;

// Request a scene sound. The sound manager (loaded only after the user enables
// audio) listens for these events; if nothing is listening it's a no-op.
const emitSfx = (name) =>
  document.dispatchEvent(new CustomEvent('scene-sfx', { detail: { name } }));

export async function init(canvas) {
  const scene = new THREE.Scene();
  const scene2 = new THREE.Scene();
  const renderer = new THREE.WebGLRenderer({ canvas, antialias: false, alpha: false });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

  const sizes = { width: canvas.clientWidth || 1, height: canvas.clientHeight || 1 };
  const RT_SCALE = 0.5;
  const rtSize = (n) => Math.max(1, Math.round(n * RT_SCALE));
  // Single-channel target: the water shader only reads .r (brush intensity),
  // so RGBA would waste 3/4 of the write/read bandwidth.
  let baseTexture = new THREE.WebGLRenderTarget(rtSize(sizes.width), rtSize(sizes.height), {
    minFilter: THREE.LinearFilter,
    magFilter: THREE.LinearFilter,
    format: THREE.RedFormat,
  });

  const zoom = 1;
  let aspect = sizes.width / sizes.height;
  let frustumHeight = 2 / zoom;
  let frustumWidth = frustumHeight * aspect;
  const camera = new THREE.OrthographicCamera(
    -frustumWidth / 2, frustumWidth / 2,
    frustumHeight / 2, -frustumHeight / 2,
    0.1, 1000,
  );
  camera.position.set(0, 0, 5);
  scene.add(camera);

  const dirLight = new THREE.DirectionalLight(0xdf9d72, 3.89);
  dirLight.position.set(5, 5, 5);
  scene2.add(dirLight);

  const hemiLight = new THREE.HemisphereLight(0xbfd6ff, 0x3d4a59, 0.8);
  scene2.add(hemiLight);

  const textureLoader = new THREE.TextureLoader();
  // Capture a readiness promise per texture (resolves on load, or on error so a
  // missing file can't strand the warmup) so the first visible frame can wait
  // for the water's textures to actually decode. See the warmup block below.
  const loadTex = (url, onLoad) => {
    let done;
    const ready = new Promise((res) => { done = res; });
    const tex = textureLoader.load(url, (t) => { onLoad?.(t); done(t); }, undefined, () => done(null));
    return { tex, ready };
  };
  const brushTexture = loadTex('/assets/brush.png').tex;
  // One plate serves both jobs: the water shader reads its green channel as the
  // name's coverage mask, and the swimmers' image light projects its colour.
  // It is a black field with the name in the nav green, so nothing but the
  // glyphs carries signal and the water colour comes from uBgFill instead.
  let bgAspect = 1;
  const plate = loadTex('/assets/PNGPNG.webp', (tex) => {
    const img = tex.image;
    if (img && img.width && img.height) {
      bgAspect = img.width / img.height;
      updateBgScale();
    }
  });
  const logoTexture = plate.tex;
  logoTexture.colorSpace = THREE.SRGBColorSpace;
  const bgTexture = logoTexture;

  const logoUniforms = {
    uLogo: { value: logoTexture },
    uLogoCenter: { value: new THREE.Vector2(0, 0) },
    uLogoSize: { value: new THREE.Vector2(2.821, 1.942) },
    uLogoIntensity: { value: 0.98 },
    uLogoSoftness: { value: 0.246 },
    uLogoDebug: { value: 0 },
    // Full-frame plane size (world units), kept in sync with the frustum in
    // resize(). Used to map a swimmer's world position into the same UV space
    // the water shader drifts, so the projected logo orbits in step with it.
    uPlaneSize: { value: new THREE.Vector2(frustumWidth, frustumHeight) },
  };

  const RIPPLE_POOL = 48;
  const MAX_ACTIVE = 32;
  const rippleGeo = new THREE.PlaneGeometry(0.4, 0.4);
  const rippleOpacityAttr = new THREE.InstancedBufferAttribute(new Float32Array(RIPPLE_POOL), 1);
  rippleOpacityAttr.setUsage(THREE.DynamicDrawUsage);
  rippleGeo.setAttribute('aOpacity', rippleOpacityAttr);
  const rippleMaterial = new THREE.ShaderMaterial({
    uniforms: { uBrush: { value: brushTexture } },
    vertexShader: `
      attribute float aOpacity;
      varying vec2 vUv;
      varying float vOpacity;
      void main() {
        vUv = uv;
        vOpacity = aOpacity;
        gl_Position = projectionMatrix * modelViewMatrix * instanceMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: `
      uniform sampler2D uBrush;
      varying vec2 vUv;
      varying float vOpacity;
      void main() {
        vec4 c = texture2D(uBrush, vUv);
        gl_FragColor = vec4(c.rgb, c.a * vOpacity);
      }
    `,
    transparent: true,
    blending: THREE.AdditiveBlending,
    depthTest: false,
    depthWrite: false,
  });
  const rippleMesh = new THREE.InstancedMesh(rippleGeo, rippleMaterial, RIPPLE_POOL);
  rippleMesh.frustumCulled = false;
  rippleMesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
  const rippleState = Array.from({ length: RIPPLE_POOL }, () => ({
    x: 0, y: 0, scale: 0, rotZ: 2 * Math.PI * Math.random(), kind: 'click', age: 0,
  }));
  const _rippleDummy = new THREE.Object3D();
  function writeRippleMatrix(i) {
    const r = rippleState[i];
    _rippleDummy.position.set(r.x, r.y, 0);
    _rippleDummy.rotation.set(0, 0, r.rotZ);
    _rippleDummy.scale.setScalar(r.scale);
    _rippleDummy.updateMatrix();
    rippleMesh.setMatrixAt(i, _rippleDummy.matrix);
  }
  for (let i = 0; i < RIPPLE_POOL; i++) writeRippleMatrix(i);
  rippleMesh.instanceMatrix.needsUpdate = true;
  scene.add(rippleMesh);

  const backgroundMaterial = new THREE.ShaderMaterial({
    side: THREE.DoubleSide,
    uniforms: {
      time: { value: 0 },
      uDisplacement: { value: null },
      resolution: { value: new THREE.Vector4() },
      uTexture: { value: bgTexture },
      uBgScale: { value: new THREE.Vector2(1, 1) },
      uBgFill: { value: new THREE.Color(0x0600c0) },
      uTexel: { value: new THREE.Vector2(1 / rtSize(sizes.width), 1 / rtSize(sizes.height)) },

      uFreq:       { value: 23 },
      uSpeed:      { value: 1.07 },
      uAmp:        { value: 0.285 },
      uBaseColor:  { value: new THREE.Color(0x000000) },
      uLightDir:   { value: new THREE.Vector3(0.43, -0.13, 0.12) },
      uDiffuse:    { value: 0.43 },
      uSpecular:   { value: 0.97 },
      uShininess:  { value: 30.0 },
      uDistortion: { value: 0 },

      // Ripple-field response: lens refraction along the RT's slope, legacy
      // brightness-swirl blend, normal lift (glints), chroma split, and the
      // rotated wave pair that breaks the standing-wave grid.
      uRefract:    { value: 0.15 },
      uSwirl:      { value: 0 },
      uRippleLift: { value: 0.5 },
      uChroma:     { value: 0.008 },
      uWave3:      { value: 0.5 },

      // Nav green (--color-accent #00FF37) passed through raw. The shader writes
      // linear values straight to the framebuffer, so the screen reads whatever
      // is here as sRGB - a THREE.Color would sRGB-decode it and land on #00FF0A.
      uInkColor:   { value: new THREE.Vector3(0x00 / 255, 0xff / 255, 0x37 / 255) },

      uTint:       { value: new THREE.Color(1, 1, 1) },
      uExposure:   { value: 1.0 },
      uContrast:   { value: 1.0 },
      uSaturation: { value: 1.0 },
    },
    vertexShader: vertex,
    fragmentShader: fragment,
  });
  let planeGeom = new THREE.PlaneGeometry(frustumWidth, frustumHeight);
  const plane = new THREE.Mesh(planeGeom, backgroundMaterial);
  scene2.add(plane);
  plane.matrixAutoUpdate = false;
  plane.updateMatrix();

  // Dark mode gets the same thin wave lines as light mode: they come from the
  // diffuse term catching steep slopes (thin streaks, so overall stays dark),
  // a wider specular (shininess down from 128), and a touch of the same UV
  // shimmer light mode uses.
  const DARK_PRESET = {
    uFreq: 23,
    uSpeed: 0.28,
    uAmp: 0.414,
    uDiffuse: 1.1,
    uSpecular: 1.1,
    uShininess: 72,
    uDistortion: 0.012,
    uRefract: 0.15,
    uSwirl: 0,
    uRippleLift: 0.5,
    uChroma: 0.008,
    uWave3: 0.5,
    uBaseColor: 0x000000,
    uLightDir: { x: 0.43, y: -0.13, z: 0.71 },
  };
  const LIGHT_PRESET = {
    uFreq: 24,
    uSpeed: 0,
    uAmp: 0.285,
    uDiffuse: 1.59,
    uSpecular: 1.08,
    uShininess: 88,
    uDistortion: 0.0188,
    uRefract: 0.15,
    uSwirl: 0,
    uRippleLift: 0.5,
    uChroma: 0.008,
    uWave3: 0.5,
    uBaseColor: 0xa0cbe4 ,
    uLightDir: { x: 0.26, y: -0.04, z: 0.57 },
  };

  let presetTween = null;
  const _colorFrom = new THREE.Color();
  const _colorTo = new THREE.Color();

  function applyPreset(theme, { animate = true } = {}) {
    const target = theme === 'light' ? LIGHT_PRESET : DARK_PRESET;
    const u = backgroundMaterial.uniforms;
    if (presetTween) { presetTween.kill(); presetTween = null; }

    const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!animate || reduced) {
      u.uFreq.value = target.uFreq;
      u.uSpeed.value = target.uSpeed;
      u.uAmp.value = target.uAmp;
      u.uDiffuse.value = target.uDiffuse;
      u.uSpecular.value = target.uSpecular;
      u.uShininess.value = target.uShininess;
      u.uDistortion.value = target.uDistortion;
      u.uRefract.value = target.uRefract;
      u.uSwirl.value = target.uSwirl;
      u.uRippleLift.value = target.uRippleLift;
      u.uChroma.value = target.uChroma;
      u.uWave3.value = target.uWave3;
      u.uBaseColor.value.set(target.uBaseColor);
      u.uLightDir.value.set(target.uLightDir.x, target.uLightDir.y, target.uLightDir.z);
      return;
    }

    _colorFrom.copy(u.uBaseColor.value);
    _colorTo.set(target.uBaseColor);

    const state = {
      uFreq: u.uFreq.value,
      uSpeed: u.uSpeed.value,
      uAmp: u.uAmp.value,
      uDiffuse: u.uDiffuse.value,
      uSpecular: u.uSpecular.value,
      uShininess: u.uShininess.value,
      uDistortion: u.uDistortion.value,
      uRefract: u.uRefract.value,
      uSwirl: u.uSwirl.value,
      uRippleLift: u.uRippleLift.value,
      uChroma: u.uChroma.value,
      uWave3: u.uWave3.value,
      lx: u.uLightDir.value.x,
      ly: u.uLightDir.value.y,
      lz: u.uLightDir.value.z,
      t: 0,
    };

    presetTween = gsap.to(state, {
      uFreq: target.uFreq,
      uSpeed: target.uSpeed,
      uAmp: target.uAmp,
      uDiffuse: target.uDiffuse,
      uSpecular: target.uSpecular,
      uShininess: target.uShininess,
      uDistortion: target.uDistortion,
      uRefract: target.uRefract,
      uSwirl: target.uSwirl,
      uRippleLift: target.uRippleLift,
      uChroma: target.uChroma,
      uWave3: target.uWave3,
      lx: target.uLightDir.x,
      ly: target.uLightDir.y,
      lz: target.uLightDir.z,
      t: 1,
      duration: 0.55,
      ease: 'power2.inOut',
      onUpdate: () => {
        u.uFreq.value = state.uFreq;
        u.uSpeed.value = state.uSpeed;
        u.uAmp.value = state.uAmp;
        u.uDiffuse.value = state.uDiffuse;
        u.uSpecular.value = state.uSpecular;
        u.uShininess.value = state.uShininess;
        u.uDistortion.value = state.uDistortion;
        u.uRefract.value = state.uRefract;
        u.uSwirl.value = state.uSwirl;
        u.uRippleLift.value = state.uRippleLift;
        u.uChroma.value = state.uChroma;
        u.uWave3.value = state.uWave3;
        u.uBaseColor.value.lerpColors(_colorFrom, _colorTo, state.t);
        u.uLightDir.value.set(state.lx, state.ly, state.lz);
      },
      onComplete: () => { presetTween = null; },
      onInterrupt: () => { presetTween = null; },
    });
  }

  const initialTheme = document.documentElement.dataset.theme === 'light' ? 'light' : 'dark';
  applyPreset(initialTheme, { animate: false });

  const onThemeChange = (e) => {
    applyPreset(e.detail?.theme === 'light' ? 'light' : 'dark', { animate: true });
  };
  document.addEventListener('themechange', onThemeChange);

  const modelScaleState = { value: 0.13 };
  function setModelScale(v) {
    modelScaleState.value = v;
    for (const s of swimmers) s.mesh.scale.setScalar(v);
  }

  const SWIM_FPS = 30;
  const SWIM_WAVE_FRAMES = [9, 66];
  const swimWaveTimes = [];
  function setWaveFrames(a, b) {
    SWIM_WAVE_FRAMES[0] = a;
    SWIM_WAVE_FRAMES[1] = b;
    const sorted = [a, b].slice().sort((x, y) => x - y);
    swimWaveTimes.length = 0;
    for (const f of sorted) swimWaveTimes.push(f / SWIM_FPS);
  }
  setWaveFrames(SWIM_WAVE_FRAMES[0], SWIM_WAVE_FRAMES[1]);

  // Swim-stroke ("wave") ripples get an eased twist: the spin ramps from a slow
  // drag to full speed, mirroring the arm pulling through water then completing
  // the stroke. Click/drag/splash ripples are untouched (fixed spin below).
  const WAVE_RIPPLE = {
    twistSpeed: 0.08,    // max per-frame rotation once the stroke is up to speed
    twistLife: 0.6,      // seconds to ramp from drag to full twist
    twistEase: 'power2.in',
  };
  let waveTwistEase = gsap.parseEase(WAVE_RIPPLE.twistEase);
  const setWaveTwistEase = (name) => {
    WAVE_RIPPLE.twistEase = name;
    waveTwistEase = gsap.parseEase(name) || gsap.parseEase('none');
  };

  // Trailing wakes: faint ripples stamped along each swimmer's path so they
  // visibly churn the water between stroke splashes. Wakes fade faster than
  // click/stroke ripples (own decay) so the steady-state count stays low.
  const WAKE = {
    enabled: true,
    step: 0.08,     // world distance between stamps
    trail: 0.05,    // stamped this far behind the body, along the heading
    scale: 0.35,
    opacity: 0.35,
    fade: 0.975,    // per-60fps-frame opacity decay (vs 0.992 for the rest)
  };

  const debug = SHOW_DEBUG_GUI
    ? await import('/js/hero/debug.js').then((m) => m.attachDebug({
        scene2, renderer, camera, dirLight, hemiLight,
        backgroundMaterial, logoUniforms, logoTexture, canvas,
        modelScaleState, setModelScale,
        SWIM_WAVE_FRAMES, setWaveFrames,
        WAVE_RIPPLE, setWaveTwistEase, WAKE,
      }))
    : null;

  function applyLogoLighting(material) {
    material.onBeforeCompile = (shader) => {
      shader.uniforms.uLogo = logoUniforms.uLogo;
      shader.uniforms.uLogoCenter = logoUniforms.uLogoCenter;
      shader.uniforms.uLogoSize = logoUniforms.uLogoSize;
      shader.uniforms.uLogoIntensity = logoUniforms.uLogoIntensity;
      shader.uniforms.uLogoSoftness = logoUniforms.uLogoSoftness;
      shader.uniforms.uLogoDebug = logoUniforms.uLogoDebug;
      // Share the water's time + distortion so the light drift can't diverge.
      shader.uniforms.uTime = backgroundMaterial.uniforms.time;
      shader.uniforms.uDistortion = backgroundMaterial.uniforms.uDistortion;
      shader.uniforms.uPlaneSize = logoUniforms.uPlaneSize;

      shader.vertexShader = shader.vertexShader
        .replace('#include <common>', `#include <common>
varying vec3 vLogoWorldPos;`)
        .replace('#include <project_vertex>', `#include <project_vertex>
vLogoWorldPos = (modelMatrix * vec4(transformed, 1.0)).xyz;`);

      shader.fragmentShader = shader.fragmentShader
        .replace('#include <common>', `#include <common>
varying vec3 vLogoWorldPos;
uniform sampler2D uLogo;
uniform vec2 uLogoCenter;
uniform vec2 uLogoSize;
uniform float uLogoIntensity;
uniform float uLogoSoftness;
uniform int uLogoDebug;
uniform float uTime;
uniform float uDistortion;
uniform vec2 uPlaneSize;`)
        .replace('#include <emissivemap_fragment>', `#include <emissivemap_fragment>
vec2 planeUv = vLogoWorldPos.xy / uPlaneSize + 0.5;
vec2 logoDrift = vec2(sin(uTime + planeUv.y), cos(uTime + planeUv.x)) * uDistortion;
vec2 logoUv = (vLogoWorldPos.xy + logoDrift * uPlaneSize - uLogoCenter) / uLogoSize + 0.5;
vec2 edge = smoothstep(vec2(0.0), vec2(uLogoSoftness), logoUv) *
            smoothstep(vec2(0.0), vec2(uLogoSoftness), 1.0 - logoUv);
float mask = edge.x * edge.y;
vec4 logoSample = texture2D(uLogo, vec2(logoUv.x, 1.0 - logoUv.y));
if (uLogoDebug == 1) {
  totalEmissiveRadiance = vec3(mask, mask * 0.2, mask);
} else if (uLogoDebug == 2) {
  totalEmissiveRadiance = logoSample.rgb * mask;
} else if (mask > 0.0) {
  totalEmissiveRadiance += logoSample.rgb * logoSample.a * mask * uLogoIntensity;
}`);
    };
    material.needsUpdate = true;
  }

  // Fewer characters on phones/tablets: 6 skinned meshes per frame is the hero's
  // biggest mobile CPU cost. Desktop (>=1024px, matching DESKTOP_MM) keeps the
  // full crowd. Read once at init — the scene doesn't rebuild swimmers on resize.
  const SWIMMER_COUNT = matchMedia('(min-width: 1024px)').matches ? 6 : 3;
  const HEADING_LERP = 0.8;
  // Smooth wander: each swimmer carries a turn-rate that drifts slowly and is
  // damped back toward zero, so heading changes are gentle arcs (no left/right
  // stutter from re-rolling random noise every frame).
  const WANDER_ACCEL = 1.2;   // how fast the turn-rate itself drifts (rad/s^2)
  const WANDER_DAMP = 0.7;    // pulls turn-rate back toward 0 so it can't run away
  const MAX_TURN_VEL = 0.45;  // cap on turn-rate (rad/s)
  const EDGE_START = 0.7;
  const EDGE_TURN_RATE = 1.5;
  // Keep swimmers from stacking. They steer away once within NEIGHBOR_RADIUS and
  // a hard pass guarantees centers never get closer than MIN_SEPARATION — small
  // enough that bodies can still bump and cross arms.
  const NEIGHBOR_RADIUS = 0.28;
  const SEPARATION_TURN = 2.0; // how hard they steer away (rad/s at full closeness)
  const MIN_SEPARATION = 0.12; // hard floor: centers never closer than this
  const FADE_DURATION = 0.6;
  const BUMP_RELEASE = MIN_SEPARATION * 1.5;
  const bumpLatch = new Uint8Array(SWIMMER_COUNT * SWIMMER_COUNT);
  const swimmers = [];
  let swimmersReady = false;
  const tempVec = new THREE.Vector3();

  const activeRipples = [];
  let ripplePoolCursor = 0;

  let held = null;
  let heldPointerId = -1;

  // User-driven water ripples (empty-water clicks). Hold/drag clustering is
  // laptop+ only; touch / sub-1024px just get a single tap ripple.
  const DESKTOP_MM = matchMedia('(min-width: 1024px)');
  const HOLD_DELAY = 0.14;        // s held before the cluster begins (also tap cutoff)
  const CLUSTER_INTERVAL = 0.09;  // s between cluster spawns (~5 alive at once)
  const CLUSTER_RADIUS = 0.06;    // world radius for random cluster offsets
  const DRAG_STEP = 0.05;         // world distance between path ripples
  const CLICK_SCALE = 0.6, CLUSTER_SCALE = 0.7, DRAG_SCALE = 0.8;
  let rpActive = false, rpPointerId = -1, rpMoved = false;
  let rpDownTime = 0, rpClusterAccum = 0;
  const rpCursor = new THREE.Vector2(); // current cursor (world), followed by cluster
  const rpLast = new THREE.Vector2();   // last path-ripple spawn pos

  const HIT_RADIUS_NDC = 0.08;
  const HELD_SCALE = 1.5;
  // Touch: a tap pops the swimmer up, then auto-throws this many seconds later.
  const POP_THROW_DELAY = 0.18;
  const _ptrWorld = new THREE.Vector3();
  const _ptrNdc = new THREE.Vector3();

  function spawnRippleAt(x, y, scale, kind = 'click', opacity = 1) {
    const i = ripplePoolCursor;
    ripplePoolCursor = (ripplePoolCursor + 1) % RIPPLE_POOL;
    const r = rippleState[i];
    r.x = x;
    r.y = y;
    r.scale = scale;
    r.kind = kind;
    r.age = 0;
    writeRippleMatrix(i);
    rippleOpacityAttr.array[i] = opacity;
    activeRipples.push(i);
    if (activeRipples.length > MAX_ACTIVE) {
      const dropped = activeRipples.shift();
      rippleOpacityAttr.array[dropped] = 0;
    }
    rippleOpacityAttr.needsUpdate = true;
    rippleMesh.instanceMatrix.needsUpdate = true;
  }

  function spawnSplash(x, y, intensity) {
    spawnRippleAt(x + (Math.random() - 0.5) * 0.05, y + (Math.random() - 0.5) * 0.05, intensity);
    spawnRippleAt(x + (Math.random() - 0.5) * 0.05, y + (Math.random() - 0.5) * 0.05, intensity * 0.75);
  }

  function respawnSwimmer(s) {
    if (s.releaseTimeline) { s.releaseTimeline.kill(); s.releaseTimeline = null; }
    if (s.pickupTween) { s.pickupTween.kill(); s.pickupTween = null; }
    if (s.jumpAction) s.jumpAction.stop();
    if (s.action) { s.action.reset(); s.action.play(); }
    s.prevActionTime = 0;
    s.state = 'swimming';
    s.group.scale.setScalar(1);
    s.group.position.set(
      (Math.random() - 0.5) * frustumWidth * 0.7,
      (Math.random() - 0.5) * frustumHeight * 0.7,
      0,
    );
    const h = Math.random() * Math.PI * 2;
    s.heading = h;
    s.targetHeading = h;
    s.turnVel = 0;
    s.group.rotation.z = h;
    s.fadeProgress = 0;
    s.fadeDelay = Math.random() * 0.3;
    s.wakeDist = Math.random() * WAKE.step;
    for (const m of s.materials) { m.transparent = true; m.opacity = 0; }
  }

  function respawnAll() {
    if (!swimmersReady) return;
    if (held) {
      try { canvas.releasePointerCapture(heldPointerId); } catch {}
      held = null;
      heldPointerId = -1;
      canvas.style.cursor = '';
    }
    if (rpActive) {
      try { canvas.releasePointerCapture(rpPointerId); } catch {}
      rpActive = false;
      rpPointerId = -1;
    }
    for (const s of swimmers) respawnSwimmer(s);
    for (const i of activeRipples) rippleOpacityAttr.array[i] = 0;
    activeRipples.length = 0;
    rippleOpacityAttr.needsUpdate = true;
  }

  function ndcToWorldXY(ndcX, ndcY, outVec) {
    outVec.set(ndcX, ndcY, 0).unproject(camera);
    outVec.z = 0;
    return outVec;
  }

  function findSwimmerAtNDC(ndcX, ndcY) {
    if (!swimmersReady) return null;
    let best = null;
    let bestDist = HIT_RADIUS_NDC;
    for (const s of swimmers) {
      if (s.state !== 'swimming') continue;
      tempVec.copy(s.group.position).project(camera);
      const dx = tempVec.x - ndcX;
      const dy = tempVec.y - ndcY;
      const d = Math.hypot(dx, dy);
      if (d < bestDist) { bestDist = d; best = s; }
    }
    return best;
  }

  function pickUpSwimmer(sw, worldX, worldY) {
    emitSfx('pickup');
    if (sw.releaseTimeline) { sw.releaseTimeline.kill(); sw.releaseTimeline = null; }
    if (sw.pickupTween) { sw.pickupTween.kill(); }
    sw.state = 'held';
    sw.heldTarget.set(worldX, worldY, 0);
    sw.lastHeldTarget.set(worldX, worldY, 0);
    sw.pickupTween = gsap.to(sw.group.scale, {
      x: HELD_SCALE, y: HELD_SCALE, z: HELD_SCALE,
      duration: 0.25, ease: 'back.out(2)',
      onComplete: () => { sw.pickupTween = null; },
    });
  }

  function releaseSwimmer(sw) {
    emitSfx('drop');
    if (sw.pickupTween) { sw.pickupTween.kill(); sw.pickupTween = null; }
    sw.state = 'releasing';
    const releaseX = sw.group.position.x;
    const releaseY = sw.group.position.y;
    const { action, jumpAction, materials, group } = sw;

    const tl = gsap.timeline({
      onComplete: () => {
        sw.releaseTimeline = null;
        sw.state = 'swimming';
        group.position.set(releaseX, releaseY, 0);
        group.scale.setScalar(1);
        for (const m of materials) m.opacity = 1;
      },
    });

    // Phase 1: airborne — jump anim, accelerating fall, shrink to normal size
    tl.add(() => {
      if (jumpAction) {
        jumpAction.reset();
        jumpAction.play();
        action.crossFadeTo(jumpAction, 0.15, false);
      }
    }, 0);
    tl.to(group.scale, { x: 1, y: 1, z: 1, duration: 0.45, ease: 'power2.in' }, 0);
    tl.to(group.position, { x: releaseX, y: releaseY, duration: 0.45, ease: 'power2.in' }, 0);

    // Phase 2: water hit — splash, crossfade back to swim, sink to invisible
    tl.add(() => {
      spawnSplash(releaseX, releaseY, 1.8);
      if (jumpAction) {
        action.reset();
        action.play();
        jumpAction.crossFadeTo(action, 0.15, false);
      }
    }, 0.45);
    tl.to(group.scale, { x: 0, y: 0, z: 0, duration: 0.35, ease: 'power1.out' }, 0.45);
    tl.to(materials, { opacity: 0, duration: 0.35, ease: 'power1.out' }, 0.45);

    // Phase 3: underwater pause — snap position back to the hit spot
    tl.add(() => {
      group.position.set(releaseX, releaseY, 0);
    }, 0.95);

    // Phase 4: re-emerge — splash, pop back up
    tl.add(() => {
      spawnSplash(releaseX, releaseY, 1.2);
    }, 1.15);
    tl.to(materials, { opacity: 1, duration: 0.3, ease: 'power1.out' }, 1.15);
    tl.to(group.scale, { x: 1, y: 1, z: 1, duration: 0.3, ease: 'back.out(1.7)' }, 1.15);

    sw.releaseTimeline = tl;
  }

  // Touch interaction: quick pick-up pop, then auto-throw. No drag, no pointer
  // capture — `held` is never set, so nothing tracks the finger.
  function tapThrow(sw, worldX, worldY) {
    if (sw.state !== 'swimming') return;
    pickUpSwimmer(sw, worldX, worldY);
    ensureJumpClip();
    gsap.delayedCall(POP_THROW_DELAY, () => {
      if (sw.state === 'held') releaseSwimmer(sw);
    });
  }

  function getNDC(e, out) {
    const rect = canvas.getBoundingClientRect();
    out.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
    out.y = -(((e.clientY - rect.top) / rect.height) * 2 - 1);
    out.z = 0;
    return out;
  }

  function onPointerDown(e) {
    if (held) return;
    getNDC(e, _ptrNdc);
    const sw = findSwimmerAtNDC(_ptrNdc.x, _ptrNdc.y);
    if (!sw) {
      // Empty water: spawn ripples instead of grabbing a swimmer.
      ndcToWorldXY(_ptrNdc.x, _ptrNdc.y, _ptrWorld);
      if (e.pointerType === 'touch' || !DESKTOP_MM.matches) {
        spawnRippleAt(_ptrWorld.x, _ptrWorld.y, CLICK_SCALE); // single tap ripple
        return;
      }
      rpActive = true;
      rpPointerId = e.pointerId;
      rpMoved = false;
      rpDownTime = performance.now();
      rpClusterAccum = 0;
      rpCursor.set(_ptrWorld.x, _ptrWorld.y);
      rpLast.copy(rpCursor);
      try { canvas.setPointerCapture(e.pointerId); } catch {}
      return;
    }
    ndcToWorldXY(_ptrNdc.x, _ptrNdc.y, _ptrWorld);

    // Touch: tap-only — pop and throw, no drag.
    if (e.pointerType === 'touch') {
      tapThrow(sw, _ptrWorld.x, _ptrWorld.y);
      return;
    }

    pickUpSwimmer(sw, _ptrWorld.x, _ptrWorld.y);
    ensureJumpClip();
    held = sw;
    heldPointerId = e.pointerId;
    canvas.style.cursor = 'grabbing';
    try { canvas.setPointerCapture(e.pointerId); } catch {}
  }

  function onPointerMove(e) {
    if (e.pointerType === 'touch') return; // touch is tap-only; no hover/drag
    getNDC(e, _ptrNdc);
    if (held) {
      const cx = Math.max(-0.95, Math.min(0.95, _ptrNdc.x));
      const cy = Math.max(-0.95, Math.min(0.95, _ptrNdc.y));
      ndcToWorldXY(cx, cy, _ptrWorld);
      held.heldTarget.set(_ptrWorld.x, _ptrWorld.y, 0);
    } else if (rpActive && e.pointerId === rpPointerId) {
      ndcToWorldXY(_ptrNdc.x, _ptrNdc.y, _ptrWorld);
      rpCursor.set(_ptrWorld.x, _ptrWorld.y);
      const dx = rpCursor.x - rpLast.x, dy = rpCursor.y - rpLast.y;
      if (dx * dx + dy * dy >= DRAG_STEP * DRAG_STEP) {
        rpMoved = true;
        spawnRippleAt(rpCursor.x, rpCursor.y, DRAG_SCALE);
        rpLast.copy(rpCursor);
      }
    } else {
      const sw = findSwimmerAtNDC(_ptrNdc.x, _ptrNdc.y);
      canvas.style.cursor = sw ? 'grab' : 'pointer'; // 'pointer' over water hints the ripple
    }
  }

  function onPointerUp(e) {
    if (rpActive && e.pointerId === rpPointerId) {
      const tap = !rpMoved && performance.now() - rpDownTime < HOLD_DELAY * 1000;
      if (tap) spawnRippleAt(rpCursor.x, rpCursor.y, CLICK_SCALE);
      rpActive = false;
      rpPointerId = -1;
      try { canvas.releasePointerCapture(e.pointerId); } catch {}
      return;
    }
    if (!held || e.pointerId !== heldPointerId) return;
    const sw = held;
    held = null;
    heldPointerId = -1;
    try { canvas.releasePointerCapture(e.pointerId); } catch {}
    releaseSwimmer(sw);
    getNDC(e, _ptrNdc);
    const hover = findSwimmerAtNDC(_ptrNdc.x, _ptrNdc.y);
    canvas.style.cursor = hover ? 'grab' : '';
  }

  canvas.addEventListener('pointerdown', onPointerDown);
  canvas.addEventListener('pointermove', onPointerMove);
  canvas.addEventListener('pointerup', onPointerUp);
  canvas.addEventListener('pointercancel', onPointerUp);

  const draco = new DRACOLoader();
  draco.setDecoderPath('/assets/draco/');
  const gltf = new GLTFLoader().setDRACOLoader(draco);

  const loadGlb = (url) => new Promise((res, rej) => gltf.load(url, res, undefined, rej));

  let jumpClip = null;
  let jumpClipPromise = null;
  function ensureJumpClip() {
    if (jumpClipPromise) return jumpClipPromise;
    jumpClipPromise = loadGlb('/assets/jump.glb').then((jumpGltf) => {
      jumpClip = jumpGltf.animations[0] ?? null;
      if (!jumpClip) return;
      for (const s of swimmers) {
        if (!s.jumpAction) {
          s.jumpAction = s.mixer.clipAction(jumpClip);
          s.jumpAction.setLoop(THREE.LoopRepeat, Infinity);
        }
      }
    }).catch((err) => console.error('[hero] jump.glb load failed:', err));
    return jumpClipPromise;
  }

  requestAnimationFrame(() => {
    loadGlb('/assets/manqWithAnim2.glb').then(async (modelGltf) => {
      const buddy = modelGltf.scene;
      buddy.rotation.set(Math.PI / 2, Math.PI / 2, 0);
      const swimClip = modelGltf.animations[0];

      for (let i = 0; i < SWIMMER_COUNT; i++) {
        const mesh = i === 0 ? buddy : cloneSkinned(buddy);
        mesh.scale.setScalar(modelScaleState.value);
        const materials = [];
        mesh.traverse((o) => {
          if (!o.isMesh) return;
          const mat = new THREE.MeshStandardMaterial({
            color: 0xffffff,
            roughness: 0.6,
            metalness: 0.0,
            transparent: true,
            opacity: 0,
          });
          applyLogoLighting(mat);
          o.material = mat;
          materials.push(mat);
        });

        const group = new THREE.Group();
        group.add(mesh);
        group.position.set(
          (Math.random() - 0.5) * frustumWidth * 0.7,
          (Math.random() - 0.5) * frustumHeight * 0.7,
          0,
        );

        const mixer = new THREE.AnimationMixer(mesh);
        const action = mixer.clipAction(swimClip);
        action.setLoop(THREE.LoopRepeat, Infinity);
        action.time = Math.random() * action.getClip().duration;
        action.timeScale = 0.85 + Math.random() * 0.3;
        action.play();

        const jumpAction = jumpClip ? mixer.clipAction(jumpClip) : null;
        if (jumpAction) jumpAction.setLoop(THREE.LoopRepeat, Infinity);

        const heading = Math.random() * Math.PI * 2;
        const sw = {
          group, mesh, mixer, action, jumpAction, materials,
          heading,
          targetHeading: heading,
          turnVel: 0,
          speed: 0.05 + Math.random() * 0.04,
          fadeProgress: 0,
          fadeDelay: Math.random() * 0.5,
          state: 'swimming',
          heldTarget: new THREE.Vector3(),
          lastHeldTarget: new THREE.Vector3(),
          releaseTimeline: null,
          pickupTween: null,
          prevActionTime: action.time,
          wakeDist: Math.random() * WAKE.step,
        };
        group.rotation.z = heading;
        // Added invisible: render() skips them, so the frame they'd otherwise
        // pop in on can't stall compiling their shader. compileAsync below still
        // traverses the graph (visibility-agnostic) and builds their skinned +
        // onBeforeCompile program, so it's cached before they're shown.
        group.visible = false;
        scene2.add(group);
        swimmers.push(sw);
      }

      // Precompile the swimmer materials off the main thread (parallel shader
      // compile where available) before revealing them, so their first render
      // finds a cached program instead of freezing a frame to build it.
      if (renderer.compileAsync) {
        try { await renderer.compileAsync(scene2, camera); }
        catch { /* a compile failure must not leave the swimmers hidden */ }
      }
      for (const s of swimmers) s.group.visible = true;

      swimmersReady = true;
      // On touch devices a tap throws immediately — preload the jump clip so the
      // very first tap already has the jump animation.
      if (matchMedia('(pointer: coarse)').matches) ensureJumpClip();
    }).catch((err) => console.error('[hero] glb load failed:', err));
  });

  function updateBgScale() {
    const screenAspect = sizes.width / sizes.height;
    const u = backgroundMaterial.uniforms.uBgScale.value;
    if (screenAspect > bgAspect) {
      u.set(screenAspect / bgAspect, 1);
    } else {
      u.set(1, bgAspect / screenAspect);
    }
  }

  let lastResizeW = 0, lastResizeH = 0;
  function resize() {
    const w = canvas.clientWidth || 1;
    const h = canvas.clientHeight || 1;
    // No-op guard: a same-size ResizeObserver fire (e.g. mobile chrome jitter)
    // must not dispose/recreate geometry and thrash the renderer. Starts at 0,0
    // so the initial resize() always runs.
    if (w === lastResizeW && h === lastResizeH) return;
    lastResizeW = w; lastResizeH = h;
    sizes.width = w;
    sizes.height = h;
    aspect = sizes.width / sizes.height;
    frustumHeight = 2 / zoom;
    frustumWidth = frustumHeight * aspect;
    camera.left = -frustumWidth / 2;
    camera.right = frustumWidth / 2;
    camera.top = frustumHeight / 2;
    camera.bottom = -frustumHeight / 2;
    camera.updateProjectionMatrix();
    renderer.setSize(sizes.width, sizes.height, false);
    baseTexture.setSize(rtSize(sizes.width), rtSize(sizes.height));
    backgroundMaterial.uniforms.uTexel.value.set(1 / rtSize(sizes.width), 1 / rtSize(sizes.height));
    plane.geometry.dispose();
    plane.geometry = new THREE.PlaneGeometry(frustumWidth, frustumHeight);
    logoUniforms.uPlaneSize.value.set(frustumWidth, frustumHeight);
    updateBgScale();
  }
  const ro = new ResizeObserver(resize);
  ro.observe(canvas);
  resize();

  const clock = new THREE.Clock();
  let running = true;
  let rtCleared = false;

  function onVisibilityChange() {
    if (document.hidden) return;
    clock.getDelta();
    respawnAll();
  }
  document.addEventListener('visibilitychange', onVisibilityChange);

  function tick() {
    if (!running) return;
    debug?.updateDebug();
    const rawDelta = clock.getDelta();
    if (rawDelta > 0.5) respawnAll();
    const delta = Math.min(rawDelta, 1 / 30);
    backgroundMaterial.uniforms.time.value += delta;

    // Pass 1 only has work while ripples are alive; skip it otherwise, after
    // one last cleared frame so the water doesn't read a stale ripple field.
    if (activeRipples.length > 0 || !rtCleared) {
      renderer.setRenderTarget(baseTexture);
      renderer.render(scene, camera);
      renderer.setRenderTarget(null);
      rtCleared = activeRipples.length === 0;
    }
    backgroundMaterial.uniforms.uDisplacement.value = baseTexture.texture;
    renderer.render(scene2, camera);

    if (swimmersReady) {
      for (const s of swimmers) {
        s.mixer.update(delta);

        if (s.state === 'swimming') {
          const curr = s.action.time;
          const prev = s.prevActionTime;
          if (curr >= prev) {
            for (const t of swimWaveTimes) {
              if (t > prev && t <= curr) spawnRippleAt(s.group.position.x, s.group.position.y, 1, 'wave');
              else if (t > curr) break;
            }
          } else {
            for (const t of swimWaveTimes) {
              if (t > prev) spawnRippleAt(s.group.position.x, s.group.position.y, 1, 'wave');
            }
            for (const t of swimWaveTimes) {
              if (t <= curr) spawnRippleAt(s.group.position.x, s.group.position.y, 1, 'wave');
              else break;
            }
          }
          s.prevActionTime = curr;
        } else {
          s.prevActionTime = s.action.time;
        }

        if (s.fadeProgress < 1) {
          if (s.fadeDelay > 0) {
            s.fadeDelay -= delta;
          } else {
            s.fadeProgress = Math.min(1, s.fadeProgress + delta / FADE_DURATION);
            const e = s.fadeProgress * s.fadeProgress * (3 - 2 * s.fadeProgress);
            for (const m of s.materials) m.opacity = e;
            if (s.fadeProgress >= 1) {
              for (const m of s.materials) m.opacity = 1;
            }
          }
        }

        if (s.state === 'swimming') {
          // Drift the turn-rate (not the heading) so direction sweeps smoothly.
          s.turnVel += (Math.random() - 0.5) * WANDER_ACCEL * delta;
          s.turnVel -= s.turnVel * WANDER_DAMP * delta;
          s.turnVel = Math.max(-MAX_TURN_VEL, Math.min(MAX_TURN_VEL, s.turnVel));
          s.targetHeading += s.turnVel * delta;
          let td = ((s.targetHeading - s.heading + Math.PI * 3) % (Math.PI * 2)) - Math.PI;
          if (Math.abs(td) > Math.PI / 3) {
            s.targetHeading = s.heading + Math.sign(td) * (Math.PI / 3);
          }

          tempVec.copy(s.group.position).project(camera);
          const edgeDist = Math.max(Math.abs(tempVec.x), Math.abs(tempVec.y));
          if (edgeDist > EDGE_START) {
            const pull = Math.min(1, (edgeDist - EDGE_START) / (1 - EDGE_START));
            const toCenter = Math.atan2(-s.group.position.y, -s.group.position.x);
            let ed = ((toCenter - s.targetHeading + Math.PI * 3) % (Math.PI * 2)) - Math.PI;
            s.targetHeading += Math.sign(ed) * Math.min(Math.abs(ed), EDGE_TURN_RATE * pull * delta);
          }

          // Steer away from nearby swimmers so they flow around each other.
          let sepX = 0, sepY = 0;
          for (const o of swimmers) {
            if (o === s || o.state === 'releasing') continue;
            const ox = s.group.position.x - o.group.position.x;
            const oy = s.group.position.y - o.group.position.y;
            const od2 = ox * ox + oy * oy;
            if (od2 > NEIGHBOR_RADIUS * NEIGHBOR_RADIUS || od2 < 1e-8) continue;
            const od = Math.sqrt(od2);
            const w = (NEIGHBOR_RADIUS - od) / NEIGHBOR_RADIUS;
            sepX += (ox / od) * w;
            sepY += (oy / od) * w;
          }
          if (sepX !== 0 || sepY !== 0) {
            const sepHeading = Math.atan2(sepY, sepX);
            const strength = Math.min(1, Math.hypot(sepX, sepY));
            let sd = ((sepHeading - s.targetHeading + Math.PI * 3) % (Math.PI * 2)) - Math.PI;
            s.targetHeading += Math.sign(sd) * Math.min(Math.abs(sd), SEPARATION_TURN * strength * delta);
          }

          let dh = ((s.targetHeading - s.heading + Math.PI * 3) % (Math.PI * 2)) - Math.PI;
          s.heading += Math.sign(dh) * Math.min(Math.abs(dh), HEADING_LERP * delta);

          s.group.position.x += Math.cos(s.heading) * s.speed * delta;
          s.group.position.y += Math.sin(s.heading) * s.speed * delta;

          s.group.rotation.z = s.heading;

          if (WAKE.enabled && s.fadeProgress >= 1) {
            s.wakeDist += s.speed * delta;
            if (s.wakeDist >= WAKE.step) {
              s.wakeDist -= WAKE.step;
              spawnRippleAt(
                s.group.position.x - Math.cos(s.heading) * WAKE.trail,
                s.group.position.y - Math.sin(s.heading) * WAKE.trail,
                WAKE.scale, 'wake', WAKE.opacity,
              );
            }
          }
        } else if (s.state === 'held') {
          const k = 1 - Math.exp(-12 * delta);
          s.group.position.x += (s.heldTarget.x - s.group.position.x) * k;
          s.group.position.y += (s.heldTarget.y - s.group.position.y) * k;

          const dx = s.heldTarget.x - s.lastHeldTarget.x;
          const dy = s.heldTarget.y - s.lastHeldTarget.y;
          if (dx * dx + dy * dy > 2.5e-5) {
            s.targetHeading = Math.atan2(dy, dx);
          }
          s.lastHeldTarget.copy(s.heldTarget);

          let dh = ((s.targetHeading - s.heading + Math.PI * 3) % (Math.PI * 2)) - Math.PI;
          s.heading += Math.sign(dh) * Math.min(Math.abs(dh), HEADING_LERP * 2 * delta);
          s.group.rotation.z = s.heading;
        }
      }

      // Hard de-overlap: never let two bodies sit on the same spot. Held/
      // releasing swimmers act as immovable obstacles (held is pinned to the
      // pointer, releasing is mid-jump) — only swimming ones get nudged.
      const min2 = MIN_SEPARATION * MIN_SEPARATION;
      const release2 = BUMP_RELEASE * BUMP_RELEASE;
      for (let i = 0; i < swimmers.length; i++) {
        const a = swimmers[i];
        if (a.state === 'releasing') continue;
        for (let j = i + 1; j < swimmers.length; j++) {
          const b = swimmers[j];
          if (b.state === 'releasing') continue;
          let dx = b.group.position.x - a.group.position.x;
          let dy = b.group.position.y - a.group.position.y;
          let d2 = dx * dx + dy * dy;
          const k = i * SWIMMER_COUNT + j;
          if (d2 >= min2) {
            if (d2 > release2) bumpLatch[k] = 0;
            continue;
          }
          if (!bumpLatch[k]) {
            bumpLatch[k] = 1;
            if (a.fadeProgress >= 1 && b.fadeProgress >= 1) emitSfx('bump');
          }
          let d = Math.sqrt(d2);
          if (d < 1e-5) {
            const ang = i * 1.2 + j;
            dx = Math.cos(ang); dy = Math.sin(ang); d = 1;
          }
          const nx = dx / d, ny = dy / d;
          const overlap = MIN_SEPARATION - d;
          const aMov = a.state === 'swimming';
          const bMov = b.state === 'swimming';
          if (aMov && bMov) {
            a.group.position.x -= nx * overlap * 0.5;
            a.group.position.y -= ny * overlap * 0.5;
            b.group.position.x += nx * overlap * 0.5;
            b.group.position.y += ny * overlap * 0.5;
          } else if (aMov) {
            a.group.position.x -= nx * overlap;
            a.group.position.y -= ny * overlap;
          } else if (bMov) {
            b.group.position.x += nx * overlap;
            b.group.position.y += ny * overlap;
          }
        }
      }
    }

    if (rpActive && DESKTOP_MM.matches &&
        performance.now() - rpDownTime >= HOLD_DELAY * 1000) {
      rpClusterAccum += delta;
      while (rpClusterAccum >= CLUSTER_INTERVAL) {
        rpClusterAccum -= CLUSTER_INTERVAL;
        const a = Math.random() * Math.PI * 2, d = Math.random() * CLUSTER_RADIUS;
        spawnRippleAt(rpCursor.x + Math.cos(a) * d, rpCursor.y + Math.sin(a) * d, CLUSTER_SCALE);
      }
    }

    if (activeRipples.length > 0) {
      // All rates are normalized to a 60fps reference (f = frames' worth of
      // time) so ripples fade/spin/grow the same on 60Hz and 165Hz displays.
      const f = delta * 60;
      const fade = Math.pow(0.992, f);
      const fadeWake = Math.pow(WAKE.fade, f);
      const keep = Math.pow(0.99, f);
      const grow = 3.0 * (1 - keep); // 3.0 = fixed point of the old s*0.99+0.03
      let w = 0;
      for (let n = 0; n < activeRipples.length; n++) {
        const i = activeRipples[n];
        const r = rippleState[i];
        const o = rippleOpacityAttr.array[i] * (r.kind === 'wake' ? fadeWake : fade);
        if (o < 0.01) {
          // Invisible: stop paying matrix writes + buffer uploads for it.
          rippleOpacityAttr.array[i] = 0;
          continue;
        }
        rippleOpacityAttr.array[i] = o;
        if (r.kind === 'wave') {
          r.age += delta;
          const t = Math.min(1, r.age / WAVE_RIPPLE.twistLife);
          r.rotZ += WAVE_RIPPLE.twistSpeed * waveTwistEase(t) * f;
        } else {
          r.rotZ += 0.05 * f;
        }
        r.scale = keep * r.scale + grow;
        writeRippleMatrix(i);
        activeRipples[w++] = i; // compact in place, keeping FIFO order
      }
      activeRipples.length = w;
      rippleMesh.instanceMatrix.needsUpdate = true;
      rippleOpacityAttr.needsUpdate = true;
    }
  }
  // Warm up before the first visible frame. Compile the water + ripple shaders
  // (off the main thread wherever KHR_parallel_shader_compile is available) and
  // wait for the water textures to decode, then draw one finished two-pass frame
  // and fade the canvas up over the poster. This removes the cold first-frame
  // compile stall and the "blank → flat → textured" pop; the poster behind the
  // transparent, opacity:0 canvas covers the gap. Swimmer materials compile
  // later, when the GLB lands — that stall is handled separately.
  if (renderer.compileAsync) {
    try {
      await Promise.all([
        renderer.compileAsync(scene2, camera),
        renderer.compileAsync(scene, camera),
      ]);
    } catch { /* a compile failure must not strand the canvas at opacity:0 */ }
  }
  await Promise.allSettled([plate.ready]);

  renderer.setRenderTarget(baseTexture);
  renderer.render(scene, camera);
  renderer.setRenderTarget(null);
  backgroundMaterial.uniforms.uDisplacement.value = baseTexture.texture;
  renderer.render(scene2, camera);
  canvas.style.opacity = '1';

  renderer.setAnimationLoop(tick);

  // Pause the loop while the hero is scrolled out of view — no point rendering
  // water nobody can see (real CPU/battery win once you scroll to works/contact).
  // Mirrors the works-ripple IntersectionObserver. Flushing the clock on return
  // discards the off-screen gap so the sim resumes smoothly instead of jumping.
  let heroVisible = true;
  const heroIo = new IntersectionObserver((entries) => {
    const vis = entries[0].isIntersecting;
    if (vis === heroVisible) return;
    heroVisible = vis;
    if (vis) {
      clock.getDelta();
      renderer.setAnimationLoop(tick);
    } else {
      renderer.setAnimationLoop(null);
    }
  }, { rootMargin: '200px' });
  heroIo.observe(canvas);

  return function stop() {
    running = false;
    renderer.setAnimationLoop(null);
    heroIo.disconnect();
    document.removeEventListener('visibilitychange', onVisibilityChange);
    document.removeEventListener('themechange', onThemeChange);
    if (presetTween) { presetTween.kill(); presetTween = null; }
    canvas.removeEventListener('pointerdown', onPointerDown);
    canvas.removeEventListener('pointermove', onPointerMove);
    canvas.removeEventListener('pointerup', onPointerUp);
    canvas.removeEventListener('pointercancel', onPointerUp);
    if (held && heldPointerId !== -1) {
      try { canvas.releasePointerCapture(heldPointerId); } catch {}
    }
    if (rpActive && rpPointerId !== -1) {
      try { canvas.releasePointerCapture(rpPointerId); } catch {}
    }
    rpActive = false;
    rpPointerId = -1;
    held = null;
    heldPointerId = -1;
    canvas.style.cursor = '';
    for (const s of swimmers) {
      s.releaseTimeline?.kill();
      s.pickupTween?.kill();
      s.mixer.stopAllAction();
      for (const m of s.materials) m.dispose();
    }
    swimmers[0]?.mesh.traverse((o) => { if (o.isMesh) o.geometry.dispose(); });
    swimmers.length = 0;
    debug?.dispose();
    ro.disconnect();
    rippleMesh.dispose();
    rippleMaterial.dispose();
    rippleGeo.dispose();
    plane.geometry.dispose();
    backgroundMaterial.dispose();
    brushTexture.dispose();
    logoTexture.dispose();   // === bgTexture, one plate serves both
    baseTexture.dispose();
    scene.clear(); scene2.clear();
    renderer.dispose();
    draco.dispose();
  };
}
